import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import InputText from "../../../components/Input/InputText";
import ErrorText from "../../../components/Typography/ErrorText";
import { showNotification } from "../../common/headerSlice";
import { addNewSpecialMessage } from "../specialMessageSlice";
import api from "../../../utils/axios";
import { triggerRefresh } from "../../../utils/refreshRegistry";

const INITIAL_SPECIAL_MESSAGE_OBJ = {
  date: "",
  title: "",
  speaker: "",
  audioUrl: "",
  videoUrl: "",
  specialMeetingId: "",
  specialMeetingName: "",
};

async function addSpecialMessage(e) {
  try {
    const newSpecialMessage = await api.post("api/special-meeting-messages", e);
    return newSpecialMessage;
  } catch (error) {
    console.error("Error adding special message:", error);
    throw error;
  }
}

async function getMeetingName(meetingId) {
  const response = await api.get(`/api/special-meetings/${meetingId}`);
  return response.data.name;
}

const SearchableSelect = ({ meetings, selectedId, onSelect, errorMessage }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = meetings.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedName = selectedId
    ? meetings.find((m) => String(m.id) === String(selectedId))?.name || ""
    : "";

  return (
    <div className={`form-control w-full mt-4 ${isOpen ? "mb-52" : ""}`} ref={ref}>
      <label className="label">
        <span className="label-text text-base-content">Special Meeting Name</span>
      </label>
      <div className="relative">
        <input
          type="text"
          className={`input input-bordered w-full ${errorMessage ? "input-error" : ""}`}
          placeholder="Search and select a meeting..."
          value={isOpen ? search : selectedName}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearch("");
          }}
        />
        {selectedName && !isOpen && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs"
            onClick={() => {
              onSelect("");
              setSearch("");
            }}
          >
            ✕
          </button>
        )}
        {isOpen && (
          <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-base-100 border border-base-300 rounded-lg shadow-lg">
            {filtered.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-primary hover:text-primary-content transition-colors ${
                    String(selectedId) === String(m.id) ? "bg-primary/10 font-semibold" : ""
                  }`}
                  onClick={() => {
                    onSelect(String(m.id));
                    setSearch("");
                    setIsOpen(false);
                  }}
                >
                  {m.name}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-4 py-2 text-sm text-base-content/50">No matches</li>
            )}
          </ul>
        )}
      </div>
      {errorMessage && (
        <label className="label">
          <span className="label-text-alt text-error">{errorMessage}</span>
        </label>
      )}
    </div>
  );
};

function AddSpecialMessageModalBody({ closeModal }) {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [specialMessageObj, setSpecialMessageObj] = useState(INITIAL_SPECIAL_MESSAGE_OBJ);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [urlKey, setUrlKey] = useState(0); // Key to force InputText re-render
  const [specialMeetings, setSpecialMeetings] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const getSpecialMeetings = async () => {
      const response = await api.get("api/special-meetings");
      setSpecialMeetings(response.data);
    };
    getSpecialMeetings();
  }, []);

  const validate = () => {
    const newErrors = {};
    const objDate = new Date(specialMessageObj.date.trim());
    if (specialMessageObj.date.trim() === "") newErrors.date = "Date is required!";
    else if (objDate.toString() === "Invalid Date") newErrors.date = "Date is invalid!";
    if (specialMessageObj.title.trim() === "") newErrors.title = "Title is required!";
    if (specialMessageObj.speaker.trim() === "") newErrors.speaker = "Speaker is required!";
    if (specialMessageObj.specialMeetingId === "") newErrors.specialMeetingId = "Special Meeting Name is required!";
    // Check that at least one of audio URL/file or video URL is provided
    if (!selectedFile && !specialMessageObj.audioUrl.trim() && (!specialMessageObj.videoUrl || !specialMessageObj.videoUrl.trim())) {
      const urlError = "Either an Audio URL/File or a Video URL is required!";
      newErrors.audioUrl = urlError;
      newErrors.videoUrl = urlError;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveNewSpecialMessage = async () => {
    if (!validate()) return;

    // If file is selected but no URL, we need to upload it first
    // If URL is provided, we can proceed directly
    let finalAudioUrl = specialMessageObj.audioUrl;
    setSaving(true);
    setServerError("");

    try {
      // Upload file first if one is selected and no URL provided
      // If URL is provided, it takes precedence over the selected file
      if (selectedFile && !specialMessageObj.audioUrl.trim()) {
        if (!specialMessageObj.date.trim()) {
          setSaving(false);
          setErrors((prev) => ({ ...prev, date: "Date is required for file upload!" }));
          return;
        }

        const formData = new FormData();
        formData.append("audioFile", selectedFile);
        formData.append("date", specialMessageObj.date);

        const uploadResponse = await api.post(
          "/api/special-meeting-messages/upload-audio",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000, // 5 minutes timeout for large file uploads
            onUploadProgress: (progressEvent) => {
              // Optional: Could show upload progress here
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              console.log(`Upload progress: ${percentCompleted}%`);
            },
          },
        );

        finalAudioUrl = uploadResponse.data.url;
        // Update the URL field to show the uploaded file's public URL
        setSpecialMessageObj({ ...specialMessageObj, audioUrl: finalAudioUrl });
        // Clear selected file so URL field becomes visible
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Force InputText to re-render with new URL value
        setUrlKey((prev) => prev + 1);
      } else if (specialMessageObj.audioUrl.trim()) {
        // Use provided URL (file will be ignored if one was selected)
        finalAudioUrl = specialMessageObj.audioUrl;
      } else if (!specialMessageObj.videoUrl || !specialMessageObj.videoUrl.trim()) {
        setSaving(false);
        setErrors((prev) => ({
          ...prev,
          audioUrl: "Either an Audio URL/File or a Video URL is required!",
        }));
        return;
      }

      // Create the message
      let newSpecialMessageObj = {
        date: specialMessageObj.date,
        title: specialMessageObj.title,
        speaker: specialMessageObj.speaker,
        audioUrl: finalAudioUrl,
        videoUrl: specialMessageObj.videoUrl,
        specialMeetingId: parseInt(specialMessageObj.specialMeetingId),
        specialMeetingName: await getMeetingName(parseInt(specialMessageObj.specialMeetingId)),
      };

      await addSpecialMessage(newSpecialMessageObj);
      dispatch(addNewSpecialMessage({ newSpecialMessageObj }));
      dispatch(showNotification({ message: "New Special Message Added!", status: 1 }));
      closeModal();
      triggerRefresh("specialMessages");
    } catch (error) {
      console.error("Error saving message:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Failed to save message. Please try again.";
      setServerError(errorMsg);
      dispatch(showNotification({ message: errorMsg, status: 0 }));
    } finally {
      setSaving(false);
    }
  };

  const updateFormValue = ({ updateType, value }) => {
    setErrors((prev) => ({ ...prev, [updateType]: "" }));
    setServerError("");
    // If URL is being updated and it has a value, clear the selected file
    if (updateType === "url" && value.trim()) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    setSpecialMessageObj({ ...specialMessageObj, [updateType]: value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const allowedTypes = [
      "audio/mpeg", "audio/mp3", "audio/wav", "audio/wave",
      "audio/x-wav", "audio/ogg", "audio/webm", "audio/mp4",
      "audio/aac", "audio/flac",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, audioUrl: "Invalid file type. Only audio files are allowed." }));
      e.target.value = ""; // Reset file input
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setErrors((prev) => ({ ...prev, audioUrl: "" }));
    // Clear URL when file is selected
    setSpecialMessageObj({ ...specialMessageObj, audioUrl: "" });
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Clear URL field when file is removed
    setSpecialMessageObj({ ...specialMessageObj, audioUrl: "" });
  };

  return (
    <>
      <InputText
        type="date"
        defaultValue={specialMessageObj.date ? specialMessageObj.date.slice(0, 10) : ""}
        updateType="date"
        containerStyle="mt-4"
        labelTitle="Date"
        updateFormValue={updateFormValue}
        errorMessage={errors.date}
      />

      <InputText
        type="text"
        defaultValue={specialMessageObj.title}
        updateType="title"
        containerStyle="mt-4"
        labelTitle="Title"
        updateFormValue={updateFormValue}
        errorMessage={errors.title}
      />

      <InputText
        type="text"
        defaultValue={specialMessageObj.speaker}
        updateType="speaker"
        containerStyle="mt-4"
        labelTitle="Speaker"
        updateFormValue={updateFormValue}
        errorMessage={errors.speaker}
      />

      <div className={`form-control w-full mt-4`}>
        <label className="label">
          <span className="label-text text-base-content">
            Upload Audio File{" "}
            {specialMessageObj.audioUrl.trim() && "(disabled - URL entered)"}
          </span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept="audio/*"
            onChange={handleFileSelect}
            disabled={saving || !!specialMessageObj.audioUrl.trim()}
            className="file-input file-input-bordered w-full max-w-xs"
          />
        </div>
        {selectedFile && (
          <label className="label">
            <span className="label-text-alt text-info">
              Selected: {selectedFile.name} (will upload on save)
            </span>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={handleRemoveFile}
              disabled={saving}
            >
              Remove
            </button>
          </label>
        )}
      </div>

      <InputText
        key={urlKey}
        type="url"
        defaultValue={specialMessageObj.audioUrl}
        updateType="audioUrl"
        containerStyle="mt-4"
        labelTitle="URL (or upload file above)"
        placeholder={
          selectedFile
            ? "File selected - URL disabled"
            : "Enter URL or upload file"
        }
        updateFormValue={updateFormValue}
        disabled={!!selectedFile || saving}
        errorMessage={errors.audioUrl}
      />

      <InputText
        key={urlKey + "_video"}
        type="url"
        defaultValue={specialMessageObj.videoUrl}
        updateType="videoUrl"
        containerStyle="mt-4"
        labelTitle="Video URL"
        placeholder="Enter URL"
        updateFormValue={updateFormValue}
        errorMessage={errors.videoUrl}
      />

      <SearchableSelect
        meetings={specialMeetings}
        selectedId={specialMessageObj.specialMeetingId}
        onSelect={(value) => updateFormValue({ updateType: "specialMeetingId", value })}
        errorMessage={errors.specialMeetingId}
      />

      {serverError && <ErrorText styleClass="mt-4">{serverError}</ErrorText>}
      <div className="modal-action">
        <button className="btn btn-ghost" onClick={() => closeModal()}>
          Cancel
        </button>
        <button
          className="btn btn-primary px-6"
          onClick={() => saveNewSpecialMessage()}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </>
  );
}

export default AddSpecialMessageModalBody;
